// 2P AI — scheda Progetti (§8.1): striscia di riepilogo (senza $), sessioni aperte, griglia progetti,
// sessioni concluse.
//
// Le SESSIONI APERTE stanno in cima, prima dei progetti (richiesta dell'operatore, 24/09): il lavoro
// in corso è quello che si riprende dal telefono, mentre i progetti sono un archivio che cambia di
// rado. Prima bisognava scorrere tutta la griglia per ritrovarle in fondo, e con decine di progetti
// dal telefono significava non ritrovarle affatto.
import { useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useProjects, useSessions, useServers, useStatus, useCloseSession } from '../lib/queries';
import { isOpen } from '../lib/sessions';
import { isToday } from '../lib/time';
import { onCardKey } from '../lib/a11y';
import { Gauge } from '../components/Gauge';
import { ProjectCard } from '../components/ProjectCard';
import { NewCard } from '../components/NewCard';
import { SessionRow } from '../components/SessionRow';
import { Icon } from '../components/Icon';

// Le stesse parole della schermata "Nuova sessione", così la leva scelta si riconosce a colpo d'occhio.
const AUTONOMIA: Record<string, string> = {
  read: 'Solo lettura',
  gate: 'Con conferma',
  auto: 'Autonomo',
  skip: 'Skip totale',
};

// Ordine delle sessioni aperte: prima chi aspetta una risposta, poi chi è a riposo (riprendibile),
// poi chi sta lavorando. Così la prima riga è sempre quella su cui l'operatore deve agire.
const URGENZA: Record<string, number> = { awaiting_approval: 0, paused: 1, queued: 1, running: 2 };

export function ProjectsTab({ serverFilter, onClearFilter }: { serverFilter: string | null; onClearFilter: () => void }) {
  const [, navigate] = useLocation();
  const [cerca, setCerca] = useState('');
  const projectsQ = useProjects();
  const sessionsQ = useSessions();
  const projects = projectsQ.data ?? [];
  const sessions = sessionsQ.data ?? [];
  // Un errore di rete e "non c'è niente" si vedevano uguali: griglia vuota, nessuna spiegazione,
  // e la sensazione di aver perso tutto. Le query non ritentano da sole (retry: false), quindi
  // serve un modo per riprovare a mano.
  const offline = projectsQ.isError || sessionsQ.isError;
  const riprova = () => { void projectsQ.refetch(); void sessionsQ.refetch(); };
  const servers = useServers().data ?? [];
  const status = useStatus().data;
  const { closeOne, closeMany } = useCloseSession();

  const serverName = (sid: string) => servers.find((s) => s.id === sid)?.name ?? sid;
  const projectName = (pid: string) => projects.find((p) => p.id === pid)?.name ?? pid.slice(0, 8);

  // A parità di urgenza, l'attività più recente in cima.
  const openSessions = sessions
    .filter((s) => isOpen(s.status))
    .sort((a, b) => {
      const d = (URGENZA[a.status] ?? 3) - (URGENZA[b.status] ?? 3);
      return d !== 0 ? d : (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at);
    });
  const closeAllOpen = () => closeMany(openSessions, `Chiudere ${openSessions.length} sessioni aperte? L'operazione interrompe il lavoro in corso.`);

  const running = status?.sessions.running ?? 0;
  const awaiting = status?.sessions.awaiting_approval ?? 0;
  const todayCount = sessions.filter((s) => isToday(s.created_at)).length;
  const srvList = status?.servers ?? [];
  const okCount = srvList.filter((s) => s.reachable).length;
  const offCount = srvList.filter((s) => !s.reachable).length;

  // Ricerca fra i progetti: con quasi quaranta siti, trovarne uno dal telefono voleva dire
  // scorrere la griglia a occhio. Cerca nel nome del progetto e in quello del server.
  const q = cerca.trim().toLowerCase();
  const shown = projects
    .filter((p) => (serverFilter ? p.server_id === serverFilter : true))
    .filter((p) => q === '' || p.name.toLowerCase().includes(q) || serverName(p.server_id).toLowerCase().includes(q));

  function projectSummary(pid: string): ReactNode {
    const own = sessions.filter((s) => s.project_id === pid);
    const act = own.filter((s) => isOpen(s.status));
    const wait = own.filter((s) => s.status === 'awaiting_approval');
    if (act.length === 0) return <span className="lo">nessuna sessione attiva</span>;
    const base = `${act.length} ${act.length === 1 ? 'attiva' : 'attive'}`;
    if (wait.length > 0) return <>{base} · <span className="hi">{wait.length} attende OK</span></>;
    return base;
  }

  // In fondo restano solo le sessioni CONCLUSE: le aperte sono già in cima e ripeterle qui
  // raddoppiava le righe (con dieci sessioni vive l'elenco "recenti" mostrava solo quelle).
  const recent = sessions.filter((s) => !isOpen(s.status)).slice(0, 8);

  return (
    <>
      {offline ? (
        <div className="note danger offline-note" role="status">
          <Icon name="wifi_off" />
          <span>Non riesco a leggere i dati dal pannello{projects.length > 0 ? ' — quello che vedi è l’ultimo stato noto' : ''}.</span>
          <button type="button" className="btn-ghost-sm" onClick={riprova}><Icon name="refresh" />Riprova</button>
        </div>
      ) : null}

      <section className="readout" aria-label="Stato generale">
        <Gauge icon="bolt" label="In corso" value={running} foot={`su ${projects.length} ${projects.length === 1 ? 'progetto' : 'progetti'}`} />
        <Gauge icon="front_hand" label="Attende il tuo OK" value={awaiting} alert foot={awaiting > 0 ? 'da rivedere' : 'tutto a posto'} />
        <Gauge icon="history" label="Sessioni oggi" value={todayCount} />
        <Gauge
          icon="dns"
          label="Server"
          value={srvList.length}
          foot={<>{okCount} ok{offCount > 0 ? <> · <span className="f-err">{offCount} offline</span></> : null}</>}
        />
      </section>

      {openSessions.length > 0 ? (
        <>
          <div className="subhead-row">
            <div className="subhead">Sessioni aperte</div>
            <button className="btn-close-all" type="button" title="Chiudi tutte le sessioni aperte" onClick={() => void closeAllOpen()}>
              <Icon name="stop_circle" /><span className="lbl">Chiudi le aperte</span><span className="cnt">{openSessions.length}</span>
            </button>
          </div>
          <section className="list" aria-label="Sessioni aperte">
            {openSessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                where={<><b>{projectName(s.project_id)}</b> · {AUTONOMIA[s.permission_mode] ?? s.permission_mode}</>}
                onOpen={() => navigate(`/s/${s.id}`)}
                onClose={() => void closeOne(s)}
                showClose
              />
            ))}
          </section>
        </>
      ) : null}

      <div className="subhead-row">
        <div className="subhead">Progetti</div>
        {projects.length >= 8 ? (
          <div className="findbox">
            <Icon name="search" />
            <input
              type="search"
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
              placeholder="Cerca progetto o server"
              aria-label="Cerca fra i progetti"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {cerca !== '' ? (
              <button type="button" className="x" aria-label="Azzera la ricerca" onClick={() => setCerca('')}><Icon name="close" /></button>
            ) : null}
          </div>
        ) : null}
      </div>
      {serverFilter ? (
        <div className="taglist filterbar">
          <span className="tagk">
            Server: {serverName(serverFilter)}
            <span className="x" role="button" tabIndex={0} aria-label="Rimuovi filtro" onClick={onClearFilter} onKeyDown={onCardKey(onClearFilter)}><Icon name="close" /></span>
          </span>
        </div>
      ) : null}
      {shown.length === 0 && q !== '' ? (
        <p className="scr-sub">Nessun progetto per «{cerca}».</p>
      ) : (
        <section className="grid">
          {shown.map((p) => (
            <ProjectCard key={p.id} project={p} serverName={serverName(p.server_id)} summary={projectSummary(p.id)} onOpen={() => navigate(`/p/${p.id}`)} />
          ))}
          {q === '' ? <NewCard label="Nuovo progetto" onClick={() => navigate('/p/new/config')} /> : null}
        </section>
      )}

      <div className="subhead">Sessioni concluse</div>
      {recent.length === 0 ? (
        <p className="scr-sub">Nessuna sessione conclusa.</p>
      ) : (
        <section className="list" aria-label="Sessioni concluse">
          {recent.map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              where={<b>{projectName(s.project_id)}</b>}
              onOpen={() => navigate(`/s/${s.id}`)}
            />
          ))}
        </section>
      )}
    </>
  );
}
